"use strict";
(() => {
  // vendor/van-1.5.5.js
  var protoOf = Object.getPrototypeOf;
  var changedStates;
  var derivedStates;
  var curDeps;
  var curNewDerives;
  var alwaysConnectedDom = { isConnected: 1 };
  var gcCycleInMs = 1e3;
  var statesToGc;
  var propSetterCache = {};
  var objProto = protoOf(alwaysConnectedDom);
  var funcProto = protoOf(protoOf);
  var _undefined;
  var addAndScheduleOnFirst = (set, s, f, waitMs) => (
    // patched to use microtasks for less latency when waitMs is not set
    (set ?? (waitMs ? setTimeout(f, waitMs) : Promise.resolve().then(f), /* @__PURE__ */ new Set())).add(s)
  );
  var runAndCaptureDeps = (f, deps, arg) => {
    let prevDeps = curDeps;
    curDeps = deps;
    try {
      return f(arg);
    } catch (e) {
      console.error(e);
      return arg;
    } finally {
      curDeps = prevDeps;
    }
  };
  var keepConnected = (l) => l.filter((b) => b._dom?.isConnected);
  var addStatesToGc = (d) => statesToGc = addAndScheduleOnFirst(statesToGc, d, () => {
    for (let s of statesToGc)
      s._bindings = keepConnected(s._bindings), s._listeners = keepConnected(s._listeners);
    statesToGc = _undefined;
  }, gcCycleInMs);
  var stateProto = {
    get val() {
      curDeps?._getters?.add(this);
      return this.rawVal;
    },
    get oldVal() {
      curDeps?._getters?.add(this);
      return this._oldVal;
    },
    set val(v) {
      curDeps?._setters?.add(this);
      if (v !== this.rawVal) {
        this.rawVal = v;
        this._bindings.length + this._listeners.length ? (derivedStates?.add(this), changedStates = addAndScheduleOnFirst(changedStates, this, updateDoms)) : this._oldVal = v;
      }
    }
  };
  var state = (initVal) => ({
    __proto__: stateProto,
    rawVal: initVal,
    _oldVal: initVal,
    _bindings: [],
    _listeners: []
  });
  var bind = (f, dom2) => {
    let deps = { _getters: /* @__PURE__ */ new Set(), _setters: /* @__PURE__ */ new Set() }, binding = { f }, prevNewDerives = curNewDerives;
    curNewDerives = [];
    let newDom = runAndCaptureDeps(f, deps, dom2);
    newDom = (newDom ?? document).nodeType ? newDom : new Text(newDom);
    for (let d of deps._getters)
      deps._setters.has(d) || (addStatesToGc(d), d._bindings.push(binding));
    for (let l of curNewDerives) l._dom = newDom;
    curNewDerives = prevNewDerives;
    return binding._dom = newDom;
  };
  var derive = (f, s = state(), dom2) => {
    let deps = { _getters: /* @__PURE__ */ new Set(), _setters: /* @__PURE__ */ new Set() }, listener = { f, s };
    listener._dom = dom2 ?? curNewDerives?.push(listener) ?? alwaysConnectedDom;
    s.val = runAndCaptureDeps(f, deps, s.rawVal);
    for (let d of deps._getters)
      deps._setters.has(d) || (addStatesToGc(d), d._listeners.push(listener));
    return s;
  };
  var add = (dom2, ...children) => {
    for (let c of children.flat(Infinity)) {
      let protoOfC = protoOf(c ?? 0);
      let child = protoOfC === stateProto ? bind(() => c.val) : protoOfC === funcProto ? bind(c) : c;
      child != _undefined && dom2.append(child);
    }
    return dom2;
  };
  var tag = (ns, name, ...args) => {
    let [{ is, ...props }, ...children] = protoOf(args[0] ?? 0) === objProto ? args : [{}, ...args];
    let dom2 = ns ? document.createElementNS(ns, name, { is }) : document.createElement(name, { is });
    for (let [k, v] of Object.entries(props)) {
      let getPropDescriptor = (proto) => proto ? Object.getOwnPropertyDescriptor(proto, k) ?? getPropDescriptor(protoOf(proto)) : _undefined;
      let cacheKey = name + "," + k;
      let propSetter = propSetterCache[cacheKey] ??= getPropDescriptor(protoOf(dom2))?.set ?? 0;
      let setter = k.startsWith("on") ? (v2, oldV) => {
        let event = k.slice(2);
        dom2.removeEventListener(event, oldV);
        dom2.addEventListener(event, v2);
      } : propSetter ? propSetter.bind(dom2) : dom2.setAttribute.bind(dom2, k);
      let protoOfV = protoOf(v ?? 0);
      k.startsWith("on") || protoOfV === funcProto && (v = derive(v), protoOfV = stateProto);
      protoOfV === stateProto ? bind(() => (setter(v.val, v._oldVal), dom2)) : setter(v);
    }
    return add(dom2, children);
  };
  var handler = (ns) => ({ get: (_, name) => tag.bind(_undefined, ns, name) });
  var update = (dom2, newDom) => newDom ? newDom !== dom2 && dom2.replaceWith(newDom) : dom2.remove();
  var updateDoms = () => {
    let iter = 0, derivedStatesArray = [...changedStates].filter((s) => s.rawVal !== s._oldVal);
    do {
      derivedStates = /* @__PURE__ */ new Set();
      for (let l of new Set(derivedStatesArray.flatMap((s) => s._listeners = keepConnected(s._listeners))))
        derive(l.f, l.s, l._dom), l._dom = _undefined;
    } while (++iter < 100 && (derivedStatesArray = [...derivedStates]).length);
    let changedStatesArray = [...changedStates].filter((s) => s.rawVal !== s._oldVal);
    changedStates = _undefined;
    for (let b of new Set(changedStatesArray.flatMap((s) => s._bindings = keepConnected(s._bindings))))
      update(b._dom, bind(b.f, b._dom)), b._dom = _undefined;
    for (let s of changedStatesArray) s._oldVal = s.rawVal;
  };
  var van_1_5_5_default = {
    tags: new Proxy((ns) => new Proxy(tag, handler(ns)), handler()),
    hydrate: (dom2, f) => update(dom2, bind(f, dom2)),
    add,
    state,
    derive
  };

  // toolbox-v2.js
  var config = function() {
    const isDebug = document.cookie.indexOf("debugTimestamp=1") !== -1;
    const isLocalhostOrIp = Boolean(location.hostname.match(/^(localhost|\[.+\]|[^a-zA-Z]+)$/i));
    const cdnPath = isLocalhostOrIp ? "" : "https://cdn.myshoptet.com/usr/" + location.host;
    return {
      cdnPath,
      snippetPath: cdnPath + "/user/documents/upload/snippets/",
      cacheBusterParam: "v",
      cacheBuster: null,
      cacheLifetime: 5 * 6e4,
      noCache: isDebug,
      tabletSlickBreakpoint: shoptet.config.breakpoints.lg,
      mobileSlickBreakpoint: shoptet.config.breakpoints.sm
    };
  }();
  var common = /* @__PURE__ */ function() {
    function constant(val) {
      return () => val;
    }
    function identity(val) {
      return val;
    }
    function property(key) {
      return (obj) => obj[key];
    }
    function matcher(keyOrProps, value) {
      if (typeof keyOrProps !== "object")
        return (obj) => obj[keyOrProps] === value;
      return (obj) => Object.entries(keyOrProps).every(([key, value2]) => obj[key] === value2);
    }
    function find(arr, predicate) {
      for (let i = 0; i < arr.length; i++)
        if (predicate(arr[i], i))
          return arr[i];
      return null;
    }
    function findIndex(arr, predicate) {
      for (let i = 0; i < arr.length; i++)
        if (predicate(arr[i], i))
          return i;
      return -1;
    }
    function indexBy(arr, iteratee) {
      var obj = {};
      for (let i = 0; i < arr.length; i++)
        obj[iteratee(arr[i], i)] = arr[i];
      return obj;
    }
    function groupBy(arr, iteratee) {
      const obj = {};
      for (let i = 0; i < arr.length; i++) {
        const key = iteratee(arr[i], i);
        if (!obj[key]) obj[key] = [];
        obj[key].push(arr[i]);
      }
      return obj;
    }
    function isPlainObject(obj) {
      return obj != null && [null, Object.prototype].includes(Object.getPrototypeOf(obj));
    }
    function isString(obj) {
      return Object.prototype.toString.call(obj) === "[object String]";
    }
    function assignDeep(targetObj, ...sourceObjs) {
      for (let sourceObj of sourceObjs) {
        for (let [key, sourceValue] of Object.entries(sourceObj)) {
          if (isPlainObject(targetObj[key])) {
            if (!isPlainObject(sourceValue))
              throw new Error("Cannot extend plain object with value of a different type.");
            assignDeep(targetObj[key], sourceValue);
          } else
            targetObj[key] = sourceValue;
        }
      }
      return targetObj;
    }
    function parseNumber(str) {
      return parseFloat(("" + str).replace(/\s+/g, "").replace(",", "."));
    }
    function parsePrice(str) {
      return parseNumber(("" + str).replace(shoptet.config.currencySymbol, "").replace(/(od|ab|from)\s/, ""));
    }
    function pluralize(n, form1, form24, formMore) {
      n = Math.abs(n);
      return n === 1 ? form1 : n >= 2 && n <= 4 ? form24 : formMore;
    }
    function slugify(str) {
      const letters = str.trim().toLowerCase().split("");
      const transFrom = "\u011B\u0161\u010D\u0159\u017E\xFD\xE1\xED\xE9\u010F\u0165\xF3\xFA\u016F\u0148\u010F\u0165";
      const transTo = "escrzyaiedtouundt";
      for (let index, j = 0; j < letters.length; j++) {
        if ((index = transFrom.indexOf(letters[j])) !== -1)
          letters[j] = transTo[index];
      }
      return letters.join("").replace(/[^a-z0-9]+/g, "-").replace(/-+/g, "-");
    }
    function getProperty(obj, path) {
      return path.split(".").reduce((cur, key) => cur == null ? cur : cur[key], obj);
    }
    function setProperty(obj, path, value) {
      var parts = path.split("."), key;
      while (key = parts.shift()) {
        if (!parts.length)
          obj[key] = value;
        else
          obj = obj[key];
      }
    }
    function once(fn) {
      let done = false;
      return (...args) => {
        if (done)
          return;
        done = true;
        return fn(...args);
      };
    }
    function debounce(fn, wait) {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), wait);
      };
    }
    function throttle(fn, wait) {
      let lastCall = 0, lastResult;
      return (...args) => {
        const now = Date.now();
        if (now - lastCall < wait)
          return lastResult;
        lastCall = now;
        return lastResult = fn(...args);
      };
    }
    function nextTick(fn) {
      Promise.resolve().then(fn);
    }
    async function promiseTry(fn) {
      return fn();
    }
    function memoize(cacheKeyResolver, callback, storage = window.sessionStorage) {
      function resolveKey(resolver, args, context) {
        let resolvedKey;
        if (typeof resolver === "function")
          resolvedKey = resolver.apply(context, args);
        else
          resolvedKey = resolver;
        if (Array.isArray(resolvedKey))
          resolvedKey = resolvedKey.map((resolver2) => resolveKey(resolver2, args, context)).join(".");
        else if (!isString(resolvedKey))
          resolvedKey = JSON.stringify(resolvedKey);
        else
          resolvedKey = "" + resolvedKey;
        return resolvedKey;
      }
      const pendingPromises = /* @__PURE__ */ Object.create({});
      return function(...args) {
        const context = this;
        const cacheKey = resolveKey(cacheKeyResolver, args, this);
        if (pendingPromises[cacheKey])
          return pendingPromises[cacheKey];
        let cachedValue = storage?.getItem(cacheKey);
        if (!config.noCache && cachedValue != null) {
          cachedValue = JSON.parse(cachedValue);
          if (cachedValue && cachedValue.t && Date.now() <= cachedValue.t + config.cacheLifetime)
            return Promise.resolve(cachedValue.v);
        }
        const promise = promiseTry(() => callback.apply(context, args)).then(function(result) {
          try {
            if (!config.noCache && cacheKey && storage)
              storage.setItem(cacheKey, JSON.stringify({ v: result, t: Date.now() }));
          } catch (err) {
            console.error("Unable to write cache key '" + cacheKey + "':", err);
          }
          return result;
        });
        pendingPromises[cacheKey] = promise;
        return promise;
      };
    }
    function iteratee$(fn, context) {
      return function() {
        const res = fn.apply(context !== void 0 ? context : this, [$(this), ...arguments]);
        return res instanceof $ ? res.get() : res;
      };
    }
    function resolveValue(value, ...args) {
      return typeof value === "function" ? value(...args) : value;
    }
    return {
      constant,
      identity,
      property,
      matcher,
      isPlainObject,
      isString,
      find,
      findIndex,
      indexBy,
      groupBy,
      iteratee$,
      assignDeep,
      parseNumber,
      parsePrice,
      pluralize,
      slugify,
      getProperty,
      setProperty,
      once,
      throttle,
      debounce,
      nextTick,
      promiseTry,
      memoize,
      resolveValue
    };
  }();
  var helpers = /* @__PURE__ */ function() {
    function getLocaleKey() {
      const dl = getShoptetDataLayer();
      return dl ? dl.language + "." + dl.currency : "";
    }
    function getStrings(moduleConfig) {
      const supportedLangs = Object.keys(moduleConfig.trans || {});
      if (!supportedLangs.length)
        throw new Error("No languages available (config.trans mising or empty)");
      if (supportedLangs.length > 1 && !moduleConfig.fallbackLang)
        throw new Error("config.fallbackLang is not set, but multiple languages are available.");
      const fallbackLang = moduleConfig.fallbackLang || supportedLangs[0];
      const dl = getShoptetDataLayer();
      const lang = dl?.language || fallbackLang;
      return moduleConfig.trans[lang] || moduleConfig.trans[fallbackLang] || {};
    }
    function uuidv4() {
      return ("10000000-1000-4000-8000" + -1e11).replace(
        /[018]/g,
        (c) => (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
      );
    }
    let idCounter = 0;
    function uniqueId(prefix) {
      const id = String(++idCounter);
      return prefix ? prefix + id : id;
    }
    function classNames(...classNames2) {
      return classNames2.flat(Infinity).filter(Boolean).join(" ");
    }
    function classNamesArray(...cns) {
      const str = classNames(cns).trim();
      return str !== "" ? str.split(/\s+/) : [];
    }
    function reactiveToggle(state2, onTrue, onFalse, dom2) {
      let lastValue, onTrue2, onFalse2;
      van.derive(() => {
        const v = Boolean(van.val(state2));
        if (lastValue === v)
          return;
        lastValue = v;
        if (v) {
          common.resolveValue(onTrue2), onTrue2 = null;
          onFalse2 = common.resolveValue(onTrue);
        } else {
          common.resolveValue(onFalse2), onFalse2 = null;
          onTrue2 = common.resolveValue(onFalse);
        }
      }, void 0, dom2);
    }
    return { getLocaleKey, getStrings, uniqueId, uuidv4, classNames, classNamesArray, reactiveToggle };
  }();
  var van = function() {
    const stateProto2 = Object.getPrototypeOf(van_1_5_5_default.state());
    const val = (v) => {
      const protoOfV = Object.getPrototypeOf(v ?? 0);
      if (protoOfV === stateProto2) return v.val;
      if (protoOfV === Function.prototype) return v();
      return v;
    };
    return {
      ...van_1_5_5_default,
      val
    };
  }();
  var dom = /* @__PURE__ */ function() {
    const oneTimeKey = "__tb_oneTime";
    const oneTimeAttrKey = "tbOneTime";
    const oneTimeCommentKey = "tb.oneTimeContents";
    function find(coll, parent = document, copy = true) {
      if (parent == null)
        return [];
      if (!parent.nodeType)
        return find(parent, void 0, true).flatMap((p) => find(coll, p, true));
      if (coll == null)
        return [];
      if (common.isString(coll))
        return copy ? Array.from(parent.querySelectorAll(coll)) : parent.querySelectorAll(coll);
      if (coll.nodeType)
        return [coll];
      if (coll.selector != null)
        return Array.from(coll);
      if (Symbol.iterator in coll)
        return copy ? Array.from(coll) : coll;
      console.log(coll);
      throw new Error(`Invalid tb.dom.find() argument: ${coll}`);
    }
    function first(coll, parent = document) {
      if (parent == null)
        return null;
      if (!parent.nodeType) {
        for (let p of find(parent, void 0, false)) {
          const el2 = first(coll, p);
          if (el2)
            return el2;
        }
        return null;
      }
      if (coll == null)
        return null;
      if (common.isString(coll))
        return parent.querySelector(coll);
      if (coll.nodeType)
        return coll;
      if (coll.selector != null)
        return coll[0] || null;
      if (Symbol.iterator in coll) {
        for (let el2 of coll)
          return el2;
        return null;
      }
      console.log(coll);
      throw new Error(`Invalid tb.dom.first() argument: ${coll}`);
    }
    function appendHelper(parent, fn, ...children) {
      parent = first(parent);
      if (!parent) return;
      parent[fn](van.add(document.createDocumentFragment(), ...children));
    }
    function append(parent, ...children) {
      appendHelper(parent, "append", ...children);
    }
    function prepend(parent, ...children) {
      appendHelper(parent, "prepend", ...children);
    }
    function replaceChildren(parent, ...children) {
      appendHelper(parent, "replaceChildren", ...children);
    }
    function insertReplaceHelper(dom2, fn, ...nodes) {
      dom2 = first(dom2);
      if (!dom2) return;
      const pl = document.createComment("");
      dom2[fn](pl);
      pl.replaceWith(van.add(document.createDocumentFragment(), ...nodes));
    }
    function after(dom2, ...nodes) {
      insertReplaceHelper(dom2, "after", ...nodes);
    }
    function before(dom2, ...nodes) {
      insertReplaceHelper(dom2, "before", ...nodes);
    }
    function replaceWith(dom2, ...nodes) {
      insertReplaceHelper(dom2, "replaceWith", ...nodes);
    }
    function trimHtml(coll, removeBrs = false) {
      for (let el2 of find(coll, void 0, false)) {
        let child;
        while (child = el2.firstChild) {
          if (child.nodeType === Node.TEXT_NODE) {
            const oldValue = child.data;
            const trimmed = oldValue.trimStart();
            if (trimmed === "") {
              child.remove();
              continue;
            } else if (oldValue !== trimmed)
              child.data = trimmed;
          } else if (child.nodeType === Node.COMMENT_NODE || removeBrs && child.tagName === "BR") {
            child.remove();
            continue;
          }
          break;
        }
        while (child = el2.lastChild) {
          if (child.nodeType === Node.TEXT_NODE) {
            const oldValue = child.data;
            const trimmed = oldValue.trimEnd();
            if (trimmed === "") {
              child.remove();
              continue;
            } else if (oldValue !== trimmed)
              child.data = trimmed;
          } else if (child.nodeType === Node.COMMENT_NODE || removeBrs && child.tagName === "BR") {
            child.remove();
            continue;
          }
          break;
        }
      }
    }
    function getTextShallow(coll, noJoin = false) {
      const parts = [];
      for (let el2 of find(coll, void 0, false)) {
        for (let c of el2.childNodes) {
          if (c.nodeType === Node.TEXT_NODE)
            parts.push(c.data);
        }
      }
      return noJoin ? parts : parts.join("");
    }
    function oneTime(coll, key) {
      const fullKey = oneTimeKey + (key ? "__" + key : "");
      let ret = false;
      for (let el2 of find(coll, void 0, false)) {
        if (!el2[fullKey]) {
          el2[fullKey] = true;
          ret = true;
        }
      }
      return ret;
    }
    function oneTimeAttr(coll, key) {
      const fullKey = oneTimeAttrKey + (key || "");
      let ret = false;
      for (let el2 of find(coll, void 0, false)) {
        if (el2.dataset[fullKey] == null) {
          el2.dataset[fullKey] = "";
          ret = true;
        }
      }
      return ret;
    }
    function oneTimeContents(coll, key) {
      const fullKey = oneTimeCommentKey + (key ? "." + key : "");
      let ret = false;
      for (let el2 of find(coll, void 0, false)) {
        let foundComment = false;
        for (let c of el2.childNodes) {
          if (c.nodeType === Node.COMMENT_NODE && c.data === fullKey) {
            foundComment = true;
            break;
          }
        }
        if (!foundComment) {
          el2.prepend(document.createComment(fullKey));
          ret = true;
        }
      }
      return ret;
    }
    function signalNativeChange(coll) {
      for (let el2 of find(coll, void 0, false))
        shoptet.scripts.signalNativeEvent("change", el2);
    }
    function createModuleInitializer(options) {
      if (typeof options === "function")
        options = { domReady: options };
      return common.once(function init(throwInitErrors) {
        try {
          for (let depObj of options.dependencies || []) {
            const { dep, catchInitErrors = false } = depObj.dep ? depObj : { dep: depObj };
            dep.init ? dep.init(!catchInitErrors) : dep();
          }
          if (options.earlyInit && options.earlyInit() === false)
            return;
          if (options.domReady)
            $(() => options.domReady());
          if (options.earlyRender)
            hooks.hookAfterDomLoad(options.earlyRender, true);
          if (options.render)
            hooks.hookAfterDomLoad(options.render, true);
          if (options.earlyRender)
            options.earlyRender();
        } catch (err) {
          const name = options.name || "<anonymous>";
          console.error(`Failed to initialize module ${name}`, err, options);
          if (options.catchInitErrors === false || throwInitErrors && options.catchInitErrors !== true)
            throw err;
          return;
        }
      });
    }
    const whenReady = createModuleInitializer;
    function waitForElement(parent, selector, { subtree = true, jq = true, first: first2 = false } = {}) {
      return new Promise((resolve) => {
        parent = dom.first(parent);
        if (!parent)
          return;
        function check() {
          const found = find(selector, parent, true);
          if (found.length) {
            const found2 = first2 ? found[0] : found;
            resolve(jq ? $(found2) : found2);
          }
          return found.length;
        }
        if (check())
          return;
        new MutationObserver((_entries, observer) => {
          if (check())
            observer.disconnect();
        }).observe(parent, { childList: true, subtree });
      });
    }
    function h(tagName, props, ...children) {
      if (!common.isPlainObject(props)) {
        children.unshift(props);
        props = {};
      }
      const parts = tagName.split(/([\.#]?[a-zA-Z0-9_:-]+)/).filter(Boolean);
      tagName = !parts.length || parts[0].charAt(0) === "." || parts[0].charAt(0) === "#" ? "div" : parts.shift();
      const classList = [];
      for (let part of parts) {
        if (part.charAt(0) === ".")
          classList.push(part.slice(1));
        else if (part.charAt(0) === "#")
          props.id = part.slice(1);
      }
      if (classList.length) {
        if (props.class) {
          const propsClass = props.class;
          props.class = () => helpers.classNames(classList, van.val(propsClass));
        } else
          props.class = classList.join(" ");
      }
      return van.tags[tagName](props, ...children);
    }
    function each(coll, iteratee, parent, filter) {
      if (typeof filter !== "function")
        filter = () => true;
      let i = 0;
      for (let el2 of find(coll, parent, false)) {
        if (filter(el2))
          iteratee(el2, i);
        i++;
      }
    }
    function eachOneTime(coll, iteratee, key, parent) {
      each(coll, iteratee, parent, (el2) => oneTime(el2, key));
    }
    function eachOneTimeAttr(coll, iteratee, key, parent) {
      each(coll, iteratee, parent, (el2) => oneTimeAttr(el2, key));
    }
    function eachOneTimeContents(coll, iteratee, key, parent) {
      each(coll, iteratee, parent, (el2) => oneTimeContents(el2, key));
    }
    function $h2(tagName, attrs, children) {
      if (!common.isPlainObject(attrs)) {
        children = attrs;
        attrs = {};
      }
      const el2 = h(tagName, attrs);
      function appendChildren(coll) {
        if (coll == null)
          return;
        if (common.isString(coll))
          el2.insertAdjacentHTML("beforeend", coll);
        else if (coll.nodeType)
          el2.append(coll);
        else if (coll.selector != null)
          el2.append(...Array.from(coll));
        else if (Array.isArray(coll))
          coll.forEach(appendChildren);
        else {
          console.log(coll);
          throw new Error(`Invalid tb.dom.$h() children: ${coll}`);
        }
      }
      if (children != null) {
        if (Array.isArray(children) || children.selector != null)
          appendChildren(children);
        else
          el2.textContent = "" + children;
      }
      return $(el2);
    }
    function $t2(text) {
      return $(document.createTextNode(text));
    }
    function $each(coll, iteratee) {
      let i = 0;
      for (let el2 of find(coll, void 0, false)) {
        if (iteratee($(el2), i++) === false)
          return false;
      }
    }
    function $eachOneTime($coll, iteratee, key) {
      return $each($coll, ($el, i) => void (oneTime($el, key) && iteratee($el, i)));
    }
    function $eachOneTimeContents($coll, iteratee, key) {
      return $each($coll, ($el, i) => void (oneTimeContents($el, key) && iteratee($el, i)));
    }
    function $map($coll, iteratee, unwrapJq = true) {
      return $($coll).map((i, el2) => {
        const res = iteratee($(el2), i);
        return unwrapJq && res instanceof $ ? res.get() : res;
      });
    }
    function $filter($coll, iteratee) {
      return $($coll).filter((i, el2) => iteratee($(el2), i));
    }
    function createDocumentFromString(html) {
      return new DOMParser().parseFromString(html, "text/html");
    }
    function createFragmentFromString(html) {
      const template = document.createElement("template");
      template.innerHTML = html;
      return template.content;
    }
    return {
      trimHtml,
      getTextShallow,
      find,
      first,
      each,
      eachOneTime,
      eachOneTimeAttr,
      eachOneTimeContents,
      append,
      prepend,
      after,
      before,
      replaceWith,
      replaceChildren,
      oneTime,
      oneTimeAttr,
      oneTimeContents,
      whenReady,
      waitForElement,
      createModuleInitializer,
      h,
      $h: $h2,
      $t: $t2,
      signalNativeChange,
      $each,
      $map,
      $filter,
      $eachOneTime,
      $eachOneTimeContents,
      createDocumentFromString,
      createFragmentFromString
    };
  }();
  var $h = dom.$h;
  var $t = dom.$t;
  var ajax = function() {
    function appendCacheBuster(url, validMs) {
      const value = config.noCache ? String(Date.now()) : `${config.cacheBuster || ""}${validMs ? "." + Math.floor(Date.now() / validMs) * validMs : ""}`;
      return url && value ? `${url}${url.includes("?") ? "&" : "?"}${config.cacheBusterParam}=${encodeURIComponent(value)}` : url;
    }
    async function loadTextNoCache(url) {
      const res = await fetch(url);
      if (!res.ok) {
        const err = new Error(`Failed to fetch ${url}: status = ${res.status}, statusText = ${res.statusText}`);
        err.fetchResponse = res;
        throw err;
      }
      return res.text();
    }
    const loadTextCached = common.memoize((url) => `ajaxCache.${helpers.getLocaleKey()}.${url}`, loadTextNoCache);
    async function loadText(url, options) {
      options = common.isPlainObject(options) ? options : { noCache: options };
      const { noCache = false } = options;
      return noCache ? loadTextNoCache(url) : loadTextCached(url);
    }
    async function loadFragment(url, options) {
      options = common.isPlainObject(options) ? options : { noCache: options, jq: true };
      const { jq = false } = options;
      const body = await loadText(url, options);
      if (jq)
        return $(body, dom.createDocumentFromString(""));
      return dom.createFragmentFromString(body);
    }
    async function loadDocument(url, options) {
      options = common.isPlainObject(options) ? options : { noCache: options, jq: true };
      const { jq = false } = options;
      const doc = new DOMParser().parseFromString(await loadText(url, options), "text/html");
      return jq ? $(doc) : doc;
    }
    async function load(urls, loader, ...loaderArgs) {
      loader = loader || loadDocument;
      if (common.isString(urls))
        return loader(urls);
      if (Array.isArray(urls))
        return Promise.all(urls.map((url) => loader(url, ...loaderArgs)));
      if (common.isPlainObject(urls))
        return Object.fromEntries(await Promise.all(Object.entries(urls).map(async ([key, url]) => [key, await loader(url, ...loaderArgs)])));
      console.log(urls);
      throw new Error(`Invalid tb.ajax.load() argument: ${urls}`);
    }
    function loadSnippets(urls, loader, ...loaderArgs) {
      loader = loader || loadFragment;
      return load(urls, (url, ...restArgs) => loader(config.snippetPath + appendCacheBuster(url), ...restArgs), ...loaderArgs);
    }
    function withSnippets(urls, callback) {
      return function(...args) {
        loadSnippets(urls).then((snippets) => {
          callback.call(this, snippets, ...args);
        });
      };
    }
    function restoreShoptetCookies() {
      const language = getShoptetDataLayer().language;
      const currencyCode = getShoptetDataLayer().currency;
      document.cookie = "language=" + language + "; max-age=31536000; path=/; secure";
      document.cookie = "currencyCode=" + currencyCode + "; max-age=31536000; path=/; secure";
    }
    const ajaxGetCached = loadTextCached;
    return {
      appendCacheBuster,
      ajaxGetCached: loadTextCached,
      loadText,
      loadFragment,
      loadDocument,
      load,
      loadSnippets,
      withSnippets,
      restoreShoptetCookies
    };
  }();
  var hooks = /* @__PURE__ */ function() {
    function hookFn(nameOrFn, callback) {
      const origFn = common.isString(nameOrFn) ? common.getProperty(window, nameOrFn) : nameOrFn;
      if (typeof origFn !== "function") {
        console.error("Failed to hook " + nameOrFn + " (not a function)");
        return;
      }
      function patchedFn() {
        let calledOrigFn = false;
        try {
          return callback(function() {
            calledOrigFn = true;
            return origFn.apply(this, arguments);
          }, arguments, this);
        } catch (err) {
          if (!calledOrigFn) {
            console.error("Patched callback for", nameOrFn, "has thrown an exception before calling origFn: ", err);
            return origFn.apply(this, arguments);
          }
          throw err;
        }
      }
      ;
      patchedFn.origFn = origFn;
      if (common.isString(nameOrFn))
        common.setProperty(window, nameOrFn, patchedFn);
      return patchedFn;
    }
    function hookAfterAjaxDone(url, callback) {
      let urlMatches;
      if (typeof url === "function")
        urlMatches = url;
      else if (url instanceof RegExp)
        urlMatches = (_url) => url.test(_url);
      else
        urlMatches = (_url) => url === _url;
      hookFn("$.ajax", (oldAjax, args, context) => {
        const options = args[0], ajaxResult = oldAjax.apply(context, args);
        if (urlMatches(options?.url, options)) {
          ajaxResult.done = hookFn(ajaxResult.done, (oldDone, args2, context2) => oldDone.apply(context2, [
            ...args2,
            function() {
              try {
                return callback.apply(this, arguments);
              } catch (err) {
                console.error("Ajax callback has thrown an exception:", err);
              }
            }
          ]));
        }
        return ajaxResult;
      });
    }
    function hookAfterDomLoad(callback, native) {
      const events = ["ShoptetDOMContentLoaded", "ShoptetDOMContentChanged", "TDOMContentLoaded"];
      function handler2(e) {
        try {
          callback.call(e ? e.target : null);
        } catch (err) {
          console.error("Dom load callback has thrown an exception:", err);
        }
      }
      for (let event of events)
        document.addEventListener(event, handler2);
      if (native)
        $(() => handler2());
    }
    function signalDomLoad(event, element) {
      let globalEvent = "TDOMContentLoaded";
      if (!event) {
        event = globalEvent;
        globalEvent = null;
      }
      shoptet.scripts.signal(event, element, null, globalEvent);
    }
    let sdlPromise = null;
    function queueSignalDomLoad() {
      sdlPromise ??= Promise.resolve().then(() => {
        sdlPromise = null;
        tb.hooks.signalDomLoad();
      });
    }
    function hookAfterWindowResize(callback, debounceTime = 250, now = true) {
      const $w = $(window);
      function boundCallback() {
        try {
          callback($w.width());
        } catch (err) {
          console.error("Resize callback has thrown an exception:", err);
        }
      }
      $w.on("resize", debounceTime > 0 ? common.debounce(boundCallback, debounceTime) : boundCallback);
      if (now)
        boundCallback();
    }
    function respond(query, onMatch, onUnmatch) {
      const media = matchMedia(query);
      let onUnmatch2, onMatch2, matches = null;
      function safeCall(f) {
        return typeof f === "function" ? f() : void 0;
      }
      function onChange(e) {
        const _matches = e.matches;
        if (matches === _matches)
          return;
        matches = _matches;
        if (matches) {
          safeCall(onMatch2), onMatch2 = null;
          onUnmatch2 = safeCall(onMatch);
        } else {
          safeCall(onUnmatch2), onUnmatch2 = null;
          onMatch2 = safeCall(onUnmatch);
        }
      }
      media.addEventListener("change", onChange);
      onChange(media);
      return media;
    }
    const reactiveMatchMediaCache = {};
    function reactiveMatchMedia(query) {
      return reactiveMatchMediaCache[query instanceof MediaQueryList ? query.media : query] ??= (() => {
        const mediaQueryList = query instanceof MediaQueryList ? query : matchMedia(query);
        const state2 = van.state(mediaQueryList.matches);
        mediaQueryList.addEventListener("change", (e) => {
          state2.val = e.matches;
        });
        return state2;
      })();
    }
    function bindEvents(eventNames, callback, now, $el) {
      $el = $($el || document);
      if (!Array.isArray(eventNames))
        eventNames = [eventNames];
      for (let name of eventNames) {
        if (name === "ready")
          $(callback);
        else
          $el[0].addEventListener(name, callback);
      }
      if (now)
        callback();
    }
    return {
      hookFn,
      hookAfterAjaxDone,
      hookAfterDomLoad,
      signalDomLoad,
      queueSignalDomLoad,
      hookAfterWindowResize,
      reactiveMatchMedia,
      respond,
      bindEvents
    };
  }();
  var sh = /* @__PURE__ */ function() {
    function splitVariantCode(code, asObject) {
      var parts = code.split("-"), res = asObject ? {} : [];
      for (var i = 0; i < parts.length; i += 2) {
        var pair = [
          common.parseNumber(parts[i]),
          common.parseNumber(parts[i + 1])
        ];
        if (asObject)
          res[pair[0]] = pair[1];
        else
          res.push(pair);
      }
      return res;
    }
    function joinVariantCode(pairs) {
      let tempVariantCode = [];
      if (Array.isArray(pairs)) {
        for (let p of pairs)
          tempVariantCode.push(String(p[0]) + "-" + String(p[1]));
      } else {
        for (let key in pairs)
          tempVariantCode.push(String(key) + "-" + String(pairs[key]));
      }
      tempVariantCode.sort();
      return tempVariantCode.join("-");
    }
    function extractVariantCode($el) {
      var className;
      if ($el instanceof $)
        className = $el.attr("class") || "";
      else
        className = $el;
      for (let c of className.split(" ")) {
        let m = c.trim().match(/^([0-9-]+)$/);
        if (m)
          return m[1];
      }
      return null;
    }
    function getNecessaryVariantData(necessaryVariantData, asObject = false) {
      if (necessaryVariantData instanceof $ || necessaryVariantData instanceof Element) {
        const scriptHtml = dom.first("script", dom.first(".detail-parameters,.p-variants-block", necessaryVariantData))?.innerHTML;
        const dataSerialized = scriptHtml?.match(/shoptet.variantsSplit.necessaryVariantData = (.+);/)?.[1];
        necessaryVariantData = dataSerialized ? JSON.parse(dataSerialized) : {};
      } else if (!common.isPlainObject(necessaryVariantData)) {
        asObject = asObject || Boolean(necessaryVariantData);
        necessaryVariantData = null;
      }
      const variants = necessaryVariantData || shoptet.variantsSplit.necessaryVariantData || {};
      for (let code in variants) {
        const variant = variants[code];
        if (variant.tb_code)
          continue;
        variant.tb_code = code;
        variant.tb_parameterValues = splitVariantCode(code, false);
        variant.tb_parameterValuesObj = splitVariantCode(code, true);
        variant.tb_price = common.parsePrice(variant.price);
        variant.tb_standardPrice = common.parsePrice(variant.standardPrice);
      }
      return asObject ? variants : Object.values(variants);
    }
    function getSelectedParameterValues(form, inclNull = false) {
      form = dom.first(form || "#product-detail-form");
      const parameterValues = {};
      dom.each(".hidden-split-parameter,.split-parameter", (param) => {
        const parameterId = Number(param.dataset.parameterId);
        const valueStr = param.tagName === "SELECT" ? param.value : param.querySelector("input:checked")?.value;
        if (valueStr || inclNull)
          parameterValues[parameterId] = valueStr ? Number(valueStr) : null;
      }, form);
      return parameterValues;
    }
    function convertImageUrl(url, size = "orig") {
      if (!["detail", "thumb", "related", "big", "orig"].includes(size))
        throw new Error(`Invalid image size: ${size}`);
      return url.replace(/\/(detail|thumb|related|orig|big)\//, `/${size}/`);
    }
    function getDescriptiveParameters(detail, asObject = false) {
      detail = dom.first(detail || ".p-detail");
      const params = [], paramsObj = {};
      dom.each(".extended-description .detail-parameters tr", (tr) => {
        const name = dom.getTextShallow(tr.querySelector(".row-header-label")).trim();
        const td = tr.querySelector("td");
        if (name)
          params.push(paramsObj[name] = { tr, $tr: $(tr), name, td, value: (td?.textContent || "").trim() });
      }, detail);
      return asObject ? paramsObj : params;
    }
    function normalizeProductUrl(url) {
      if (!common.isString(url))
        return null;
      url = url.toLowerCase().trim();
      if (url === "")
        return null;
      if (!url.match(/^(http|\/)/))
        url = "/" + url;
      try {
        url = new URL(url, location.href).pathname;
        return url.endsWith("/") ? url : url + "/";
      } catch (err) {
        return null;
      }
    }
    return {
      splitVariantCode,
      joinVariantCode,
      extractVariantCode,
      getNecessaryVariantData,
      getSelectedParameterValues,
      getDescriptiveParameters,
      convertImageUrl,
      normalizeProductUrl
    };
  }();
  var ui = function() {
    function wrapColorboxHtml($elOrHtml) {
      if (typeof $elOrHtml === "string")
        return shoptet.content.colorboxHeader + $elOrHtml + shoptet.content.colorboxFooter;
      const wrapHtml = shoptet.content.colorboxHeader + shoptet.content.colorboxFooter;
      return wrapHtml ? $(wrapHtml).html($elOrHtml) : $($elOrHtml);
    }
    async function loadAndOpenModal(url, selectorOrTransformer, optionsOrSize = {}) {
      const transformer = typeof selectorOrTransformer === "string" ? ($page2) => $page2.find(selectorOrTransformer) : selectorOrTransformer;
      const options = typeof optionsOrSize === "string" ? { size: optionsOrSize } : optionsOrSize || {};
      const size = !options.size && options.size !== null ? "lg" : options.size;
      delete options.size;
      if (size !== null) {
        const sizeSuffix = size.slice(0, 1).toUpperCase() + size.slice(1).toLowerCase();
        if (options.width === void 0)
          options.width = shoptet.modal.config["width" + sizeSuffix];
        options.className = `${shoptet.modal.config["class" + sizeSuffix]} ${options.className || ""}`;
      }
      const $page = await ajax.loadDocument(url);
      const $content = $(transformer($page));
      if (!$content.length)
        throw new Error(`Element not found (url: ${url})`);
      return new Promise((resolve) => {
        shoptet.modal.open({
          ...options,
          html: wrapColorboxHtml($content),
          onComplete() {
            if (options.onComplete?.() !== false) {
              shoptet.images.unveil();
              $("body").removeClass(shoptet.config.bodyClasses);
              hideSpinner();
              setTimeout(() => {
                shoptet.modal.shoptetResize();
              }, 1);
              hooks.signalDomLoad();
            }
            resolve();
          }
        });
      });
    }
    function responsiveSlick(breakpoint, els, options) {
      els = dom.find(els).filter((el2) => dom.oneTime(el2, "responsiveSlick"));
      if (typeof breakpoint !== "function") {
        const maxWidth = breakpoint;
        breakpoint = function(width) {
          return width <= maxWidth;
        };
      }
      let oldVisible = null;
      hooks.hookAfterWindowResize(() => {
        const visible = !!breakpoint($(window).width());
        if (visible === oldVisible)
          return;
        els = els.filter((el2) => el2.isConnected);
        if (!els.length)
          return;
        try {
          if (visible)
            $(el).slick(options);
          else if (oldVisible)
            $(el).slick("destroy");
        } finally {
          oldVisible = visible;
        }
      });
    }
    const tabletSlick = responsiveSlick.bind(null, config.tabletSlickBreakpoint);
    const mobileSlick = responsiveSlick.bind(null, config.mobileSlickBreakpoint);
    return {
      wrapColorboxHtml,
      loadAndOpenModal,
      responsiveSlick,
      tabletSlick,
      mobileSlick
    };
  }();
  var JsonStorage = class _JsonStorage {
    /**
     * @param {string} key - Storage key.
     * @param {() => T} [defaultFactory] - Factory returning the default value when the key is absent.
     * @param {Storage} [backend=window.sessionStorage]
     */
    constructor(key, defaultFactory = () => ({}), backend = window.sessionStorage) {
      if (!common.isString(key))
        throw new Error("Storage key must be a string.");
      this.key = key;
      this.defaultFactory = defaultFactory;
      this.backend = backend;
    }
    /**
     * Creates a `JsonStorage` backed by `localStorage`.
     * @template T
     * @param {string} key
     * @param {() => T} [defaultFactory]
     * @returns {JsonStorage<T>}
     */
    static local(key, defaultFactory = () => ({})) {
      return new _JsonStorage(key, defaultFactory, window.localStorage);
    }
    /**
     * Creates a `JsonStorage` backed by `sessionStorage`.
     * @template T
     * @param {string} key
     * @param {() => T} [defaultFactory]
     * @returns {JsonStorage<T>}
     */
    static session(key, defaultFactory = () => ({})) {
      return new _JsonStorage(key, defaultFactory, window.sessionStorage);
    }
    /**
     * Reads and deserialises the stored value, or returns the default factory result if absent.
     * @returns {T}
     */
    get() {
      const rawValue = this.backend.getItem(this.key);
      return rawValue === null ? this.defaultFactory() : JSON.parse(rawValue);
    }
    /**
     * Serialises and writes `obj` to the backend.
     * @param {T} obj
     */
    set(obj) {
      this.backend.setItem(this.key, JSON.stringify(obj));
    }
    /**
     * Removes the stored key from the backend.
     */
    clear() {
      this.backend.removeItem(this.key);
    }
    /**
     * Reads the current value, passes it to `f`, writes the result back, and returns it.
     * @param {(current: T) => T} f
     * @returns {T}
     */
    updateAndGet(f) {
      const obj = f(this.get());
      this.set(obj);
      return obj;
    }
    /**
     * @deprecated Use {@link updateAndGet} instead.
     * @param {(current: T) => T} f
     * @returns {T}
     */
    getAndUpdate(f) {
      return this.updateAndGet(f);
    }
  };
  var toolbox = {
    config,
    common,
    helpers,
    van,
    dom,
    $h,
    $t,
    ajax,
    hooks,
    sh,
    ui,
    JsonStorage
  };
  var tb = toolbox;
  if (window.toolbox)
    console.error("toolbox is already loaded, original:", window.toolbox);
  window.toolbox = window.tb = toolbox;
})();
